"use client";

/**
 * The pane's chat thread: a narrow-column version of the web app's Thread.
 *
 * Built from the same assistant-ui primitives and the copied parts
 * (MarkdownText, ToolActivityList, ThreadPlan, the question card), without
 * the web app's branch picker, message editing, attachments toolbar and
 * editor hooks. A task pane is 320-450px wide, so everything is single
 * column and the composer sits at the bottom of the scroll viewport.
 */

import {
  ActionBarPrimitive,
  ComposerPrimitive,
  ErrorPrimitive,
  MessagePartPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useAui,
  useAuiState,
  useThreadRuntime
} from "@assistant-ui/react";
import { ArrowDownIcon, ArrowUpIcon, CheckIcon, CopyIcon, SquareIcon } from "lucide-react";
import {
  type FC,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import PromptRecipesPopup, { SlashGlyph } from "@/agents-v2/components/agents/prompt-recipes-popup";
import {
  PROMPT_RECIPES,
  RECIPE_TEXT_SEPARATOR,
  matchRecipePrefix,
  type PromptRecipe
} from "@/agents-v2/components/agents/prompt-recipes";
import { RecipeChip } from "@/agents-v2/components/agents/recipe-chip";
import { publishRecipeId } from "@/agents-v2/recipe-metadata/recipe-metadata";
import { ComposerAddAttachment, ComposerAttachments } from "@/agents-v2/components/assistant-ui/attachment";
import { UserMessageFiles } from "@/agents-v2/components/assistant-ui/user-message-files";
import { EXPORT_ARTIFACT_TOOL_NAME } from "@/agents-v2/components/common/export-artifact-tool-name";
import { ExportArtifactToolUI } from "@/agents-v2/components/common/generated-downloads";

import { resolveComposerEnter } from "@/agents-v2/components/assistant-ui/composer-enter";
import { trackMessageSent } from "../../../taskpane/analytics";
import { MarkdownText } from "@/agents-v2/components/assistant-ui/markdown-text";
import {
  AskQuestionToolUI,
  QuestionResponseSynchronizer,
  usePendingQuestionAnswerCount
} from "@/agents-v2/components/assistant-ui/thread-questions";
import { useThreadStalled } from "@/agents-v2/components/assistant-ui/stalled-run";
import { ThreadPlan } from "@/agents-v2/components/assistant-ui/thread-todos";
import { ToolActivityList } from "@/agents-v2/components/assistant-ui/tool-activity";
import { TooltipIconButton } from "@/agents-v2/components/assistant-ui/tooltip-icon-button";
import { Button } from "@/agents-v2/components/ui/button";
import {
  AGENT_QUESTION_RESPONSES_METADATA_KEY,
  createMetadataOnlyMessageContent,
  isHiddenMetadataOnlyPlaceholder
} from "@/agents-v2/message-metadata/metadata-only-message";
import { ASK_QUESTION_TOOL_NAME } from "@/agents-v2/questions/question-responses";
import { useAgentsChat } from "@/agents-v2/components/common/chat-provider";
import { cn } from "@/agents-v2/utils/cn";

/** Tool calls that render their own card in the message instead of an activity row. */
const INLINE_TOOL_UI_NAMES = new Set<string>([ASK_QUESTION_TOOL_NAME, EXPORT_ARTIFACT_TOOL_NAME]);

/** Every recipe a sent message may start with: the pane's curated list plus the shared catalogue. */
const RecipesContext = createContext<PromptRecipe[]>(PROMPT_RECIPES);
const EMPTY_NAMES: readonly string[] = [];
const InlineToolNamesContext = createContext<readonly string[]>(EMPTY_NAMES);
const useIsInlineTool = () => {
  const extra = useContext(InlineToolNamesContext);
  return (name: string) => INLINE_TOOL_UI_NAMES.has(name) || extra.includes(name);
};

export type ThreadSuggestion = { title: string; prompt: string };

export type ThreadProps = {
  /** Shown above the suggestions on an empty thread. */
  welcome?: ReactNode;
  suggestions?: ThreadSuggestion[];
  placeholder?: string;
  /** Rendered under the composer (banners, status). */
  footer?: ReactNode;
  /** Rendered directly above the composer (context chips, floating actions). */
  aboveComposer?: ReactNode;
  /** Extra controls in the composer's action row (sources, etc.). */
  composerControls?: ReactNode;
  /** The "/" recipes to offer; none hides the picker. */
  recipes?: PromptRecipe[];
  onCancel?: () => void;
  /** Extra assistant tool UIs to mount (rendered inline for `inlineToolNames`). */
  toolUIs?: ReactNode;
  inlineToolNames?: readonly string[];
};

export const Thread: FC<ThreadProps> = ({
  welcome,
  suggestions = [],
  placeholder = "Ask io.ai about this bid…",
  footer,
  aboveComposer,
  composerControls,
  recipes,
  onCancel,
  toolUIs,
  inlineToolNames
}) => (
  <ThreadPrimitive.Root className="aui-root aui-thread-root flex h-full flex-col bg-background">
    <AskQuestionToolUI />
    <ExportArtifactToolUI />
    {toolUIs}
    <QuestionResponseSynchronizer />
    <ThreadPrimitive.Viewport className="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden scroll-smooth">
      <div className="mx-auto flex w-full flex-1 flex-col px-3 pt-3">
        <ThreadPrimitive.Empty>
          <ThreadWelcome welcome={welcome} suggestions={suggestions} />
        </ThreadPrimitive.Empty>
        <InlineToolNamesContext.Provider value={inlineToolNames ?? EMPTY_NAMES}>
          <RecipesContext.Provider value={recipes ? [...recipes, ...PROMPT_RECIPES] : PROMPT_RECIPES}>
            <ThreadPrimitive.Messages components={{ UserMessage, AssistantMessage }} />
          </RecipesContext.Provider>
        </InlineToolNamesContext.Provider>
        <PendingAssistantMessage />
        <ContextCompactionActivity />
        <ThreadPrimitive.If empty={false}>
          <div className="min-h-6 grow" />
        </ThreadPrimitive.If>
        <ThreadPrimitive.ViewportFooter className="sticky bottom-0 mt-2 flex w-full flex-col items-stretch bg-background pb-3">
          <ScrollToBottom />
          {aboveComposer}
          <Composer placeholder={placeholder} onCancel={onCancel} controls={composerControls} recipes={recipes} />
          {footer}
        </ThreadPrimitive.ViewportFooter>
      </div>
    </ThreadPrimitive.Viewport>
  </ThreadPrimitive.Root>
);

const ThreadWelcome: FC<{ welcome?: ReactNode; suggestions: ThreadSuggestion[] }> = ({
  welcome,
  suggestions
}) => (
  <div className="my-auto flex grow flex-col justify-center gap-4 pb-4">
    {welcome}
    {suggestions.length > 0 && (
      <div className="flex flex-col gap-2">
        {suggestions.map((suggestion) => (
          <ThreadPrimitive.Suggestion
            key={suggestion.title}
            prompt={suggestion.prompt}
            method="replace"
            autoSend
            asChild
          >
            <Button
              variant="outline"
              className="h-auto w-full items-start justify-start whitespace-normal rounded-md border-line px-3 py-2 text-left text-sm font-normal text-fg-2 hover:bg-subtle hover:text-fg"
              onClick={() => trackMessageSent("suggestion")}
            >
              {suggestion.title}
            </Button>
          </ThreadPrimitive.Suggestion>
        ))}
      </div>
    )}
  </div>
);

const ScrollToBottom: FC = () => (
  <ThreadPrimitive.ScrollToBottom asChild>
    <TooltipIconButton
      tooltip="Scroll to bottom"
      variant="outline"
      className="absolute -top-10 right-0 z-10 rounded-full border-line bg-surface p-3 shadow-pop disabled:invisible"
    >
      <ArrowDownIcon className="size-4" />
    </TooltipIconButton>
  </ThreadPrimitive.ScrollToBottom>
);

/**
 * The composer. Typing stays enabled while the agent streams so the next
 * message can be drafted; Enter and Send are gated because a run is
 * single-threaded (Stop replaces Send). Answered question cards can be sent
 * with no typed text, as a metadata-only message the backend understands.
 *
 * Recipes work as in the web app's composer: a lone "/" (or the slash button)
 * opens the picker; choosing a recipe puts its chip on the input's first line
 * and frees the input for extra context; Backspace at the start removes the
 * chip; Cmd/Ctrl+Z undoes a chip change while the input is empty; Send (or
 * Enter) composes the recipe's full prompt plus the extra text and tags the
 * message with the recipe id. The history then shows the chip, not the prompt.
 */
const CHIP_TEXT_GAP = 4;

const Composer: FC<{
  placeholder: string;
  onCancel?: () => void;
  controls?: ReactNode;
  recipes?: PromptRecipe[];
}> = ({ placeholder, onCancel, controls, recipes }) => {
  const aui = useAui();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const chipRef = useRef<HTMLDivElement>(null);
  const [recipesOpen, setRecipesOpen] = useState(false);
  const hasRecipes = Boolean(recipes?.length);

  // The selected recipe, with its own undo/redo: the textarea's native undo
  // knows nothing about the chip, so a backspaced skill would be gone for good.
  const [recipe, setRecipe] = useState<PromptRecipe | null>(null);
  const recipeRef = useRef<PromptRecipe | null>(null);
  recipeRef.current = recipe;
  const undoStackRef = useRef<(PromptRecipe | null)[]>([]);
  const redoStackRef = useRef<(PromptRecipe | null)[]>([]);
  const commitRecipe = useCallback((next: PromptRecipe | null) => {
    undoStackRef.current.push(recipeRef.current);
    redoStackRef.current = [];
    setRecipe(next);
  }, []);
  const undoRecipe = useCallback((): boolean => {
    if (!undoStackRef.current.length) return false;
    redoStackRef.current.push(recipeRef.current);
    setRecipe(undoStackRef.current.pop() ?? null);
    return true;
  }, []);
  const redoRecipe = useCallback((): boolean => {
    if (!redoStackRef.current.length) return false;
    undoStackRef.current.push(recipeRef.current);
    setRecipe(redoStackRef.current.pop() ?? null);
    return true;
  }, []);

  const focusInput = () => requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
  const selectRecipe = (selected: PromptRecipe) => {
    commitRecipe(selected);
    // The "/" that opened the picker goes; the chip now stands for the recipe.
    aui.thread().composer().setText("");
    setRecipesOpen(false);
    focusInput();
  };

  // The chip overlays the input's first line: indent that line by its width
  // so typed text starts after it; wrapped lines return to the left edge.
  // react-textarea-autosize only re-measures on value change or window
  // resize, and text-indent changes the wrapping, so poke it.
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    const recalc = () => window.dispatchEvent(new Event("resize"));
    if (!recipe || !chipRef.current) {
      input.style.textIndent = "";
      recalc();
      return;
    }
    const chip = chipRef.current;
    const apply = () => {
      input.style.textIndent = `${chip.offsetWidth + CHIP_TEXT_GAP}px`;
      recalc();
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(chip);
    return () => {
      observer.disconnect();
      input.style.textIndent = "";
      recalc();
    };
  }, [recipe]);

  const isRunning = useAuiState((s) => s.thread.isRunning);
  const composerCanSend = useAuiState((s) => s.composer.canSend);
  const pendingAnswers = usePendingQuestionAnswerCount();
  const answersPending = pendingAnswers > 0;
  const [isForcedSending, setIsForcedSending] = useState(false);
  const forcedSendingRef = useRef(false);
  useEffect(() => {
    if (!isRunning) {
      forcedSendingRef.current = false;
      setIsForcedSending(false);
    }
  }, [isRunning]);

  /** Send a selected recipe as its full prompt plus any typed text. True when it handled the send. */
  const sendRecipe = useCallback((): boolean => {
    const selected = recipeRef.current;
    if (!selected) return false;
    const composer = aui.thread().composer();
    const extra = (composer.getState().text ?? "").trim();
    composer.setText(extra ? `${selected.prompt}${RECIPE_TEXT_SEPARATOR}${extra}` : selected.prompt);
    // Consumed when the runtime prepares the human message (see the pane's
    // prepareMessages), so it survives a slow upload.
    publishRecipeId(selected.id);
    trackMessageSent("recipe");
    setRecipe(null);
    undoStackRef.current = [];
    redoStackRef.current = [];
    requestAnimationFrame(() => composer.send());
    return true;
  }, [aui]);

  const sendAnswers = () => {
    if (sendRecipe()) return;
    if (composerCanSend) {
      trackMessageSent("composer");
      aui.thread().composer().send();
      return;
    }
    if (!answersPending || forcedSendingRef.current) return;
    forcedSendingRef.current = true;
    setIsForcedSending(true);
    trackMessageSent("answers");
    aui.thread().append({
      role: "user",
      content: createMetadataOnlyMessageContent(),
      metadata: { custom: { [AGENT_QUESTION_RESPONSES_METADATA_KEY]: [] } }
    });
  };

  const forceSend = Boolean(recipe) || answersPending;
  const sendClassName =
    "size-8 rounded-full bg-accent text-fg-inverse hover:bg-accent-hover disabled:opacity-40";

  return (
    <ComposerPrimitive.Root className="relative z-10 flex w-full flex-col">
      {hasRecipes && (
        <PromptRecipesPopup
          open={recipesOpen}
          onClose={() => {
            setRecipesOpen(false);
            focusInput();
          }}
          onSelect={selectRecipe}
          textareaRef={inputRef}
          primaryCategory="write"
          recipes={recipes}
          anchorRef={shellRef}
        />
      )}
      <div
        ref={shellRef}
        className="flex w-full flex-col rounded-lg border border-line bg-surface px-3 py-2 transition-colors duration-fast focus-within:border-accent focus-within:shadow-[0_0_0_3px_var(--accent-ring)]"
      >
        <ComposerAttachments />
        <div className="relative">
          {recipe && (
            <div
              ref={chipRef}
              className="pointer-events-none absolute left-1 top-1 z-10 flex max-w-[calc(100%-0.5rem)] items-center"
            >
              <RecipeChip title={recipe.title} />
            </div>
          )}
          <ComposerPrimitive.Input
            ref={inputRef}
            placeholder={recipe ? "" : hasRecipes ? `${placeholder} Type / for recipes.` : placeholder}
            onChange={(e) => {
              // A lone "/" opens the recipes, as in the web app's composer.
              if (hasRecipes && e.target.value === "/") setRecipesOpen(true);
              else if (recipesOpen && e.target.value !== "/") setRecipesOpen(false);
            }}
            className="max-h-32 min-h-[36px] w-full resize-none bg-transparent px-1 pt-1 text-base leading-relaxed outline-none placeholder:text-fg-4"
            rows={1}
            autoFocus
            aria-label="Message io.ai"
            onKeyDown={(e) => {
              const input = e.currentTarget;
              // Backspace at the very start removes the chip, token-style.
              if (e.key === "Backspace" && recipe && input.selectionStart === 0 && input.selectionEnd === 0) {
                e.preventDefault();
                commitRecipe(null);
                return;
              }
              // Undo/redo the chip while the input is empty (native text undo wins otherwise).
              if ((e.metaKey || e.ctrlKey) && input.value === "") {
                const key = e.key.toLowerCase();
                const isUndo = key === "z" && !e.shiftKey;
                const isRedo = (key === "z" && e.shiftKey) || (e.ctrlKey && key === "y");
                if ((isUndo && undoRecipe()) || (isRedo && redoRecipe())) {
                  e.preventDefault();
                  return;
                }
              }
              const action = resolveComposerEnter(
                { key: e.key, shiftKey: e.shiftKey, isComposing: e.nativeEvent.isComposing },
                isRunning
              );
              if (action === "ignore") return;
              if (action === "blockWhileRunning") {
                e.preventDefault();
                return;
              }
              if (sendRecipe()) {
                e.preventDefault();
                return;
              }
              if (!composerCanSend && answersPending) {
                e.preventDefault();
                sendAnswers();
                return;
              }
              if (composerCanSend) trackMessageSent("composer");
            }}
          />
        </div>
        <div className="mt-1 flex items-center justify-between">
          <div className="flex items-center gap-1">
            <ComposerAddAttachment />
            {hasRecipes && (
              <TooltipIconButton
                tooltip="Recipes"
                side="top"
                type="button"
                className="size-8 rounded-full"
                onClick={() => setRecipesOpen((open) => !open)}
              >
                <SlashGlyph />
              </TooltipIconButton>
            )}
            {controls}
          </div>
          <ThreadPrimitive.If running={false}>
            {forceSend ? (
              // A selected recipe (or answered cards) can be sent with no typed
              // text: bypass the built-in Send, which disables on an empty input.
              <TooltipIconButton
                tooltip={recipe ? "Send recipe" : "Send answers"}
                side="top"
                type="button"
                className={sendClassName}
                disabled={isForcedSending}
                onClick={sendAnswers}
              >
                <ArrowUpIcon className="size-4" />
              </TooltipIconButton>
            ) : (
              <ComposerPrimitive.Send asChild>
                <TooltipIconButton
                  tooltip="Send"
                  side="top"
                  type="button"
                  className={sendClassName}
                  onClick={() => trackMessageSent("composer")}
                >
                  <ArrowUpIcon className="size-4" />
                </TooltipIconButton>
              </ComposerPrimitive.Send>
            )}
          </ThreadPrimitive.If>
          <ThreadPrimitive.If running>
            <ComposerPrimitive.Cancel asChild>
              <TooltipIconButton
                tooltip="Stop"
                side="top"
                type="button"
                variant="outline"
                className="size-8 rounded-full"
                onClick={onCancel}
              >
                <SquareIcon className="size-3 fill-current" />
              </TooltipIconButton>
            </ComposerPrimitive.Cancel>
          </ThreadPrimitive.If>
        </div>
      </div>
    </ComposerPrimitive.Root>
  );
};

/**
 * A sent recipe shows as its chip ("/" keycap + title) plus any extra text,
 * as in the web app: the agent still received the full prompt, only the
 * history is shortened. Any other text renders as assistant-ui's default.
 */
const UserMessageText: FC = () => {
  const text = useAuiState((state) => (state.part.type === "text" ? state.part.text : ""));
  const recipes = useContext(RecipesContext);
  const match = matchRecipePrefix(text, recipes);
  if (match) {
    return (
      <p className="whitespace-pre-line">
        <RecipeChip title={match.title} className="me-2 align-middle" />
        {match.rest}
      </p>
    );
  }
  return (
    <p className="whitespace-pre-line">
      <MessagePartPrimitive.Text />
    </p>
  );
};

const UserMessage: FC = () => {
  const parts = useAuiState((state) => state.message.parts);
  const hidden = useMemo(() => isHiddenMetadataOnlyPlaceholder(parts), [parts]);
  if (hidden) return null;
  return (
    <>
      <CompactionDividerAbove />
      <MessagePrimitive.Root className="mb-3 flex w-full flex-col items-end gap-1">
        <UserMessageFiles />
      <div className="max-w-[88%] break-words rounded-md rounded-br-sm bg-muted px-3 py-2 text-base text-fg">
        <MessagePrimitive.Parts components={{ Text: UserMessageText }} />
      </div>
      </MessagePrimitive.Root>
    </>
  );
};

/**
 * Same grouping as the web app: consecutive tool calls (and reasoning)
 * collapse into one chain-of-thought block rendered as the plan plus the
 * activity list; text renders as markdown; inline tool UIs (the question
 * card) render in place once their arguments are complete.
 */
const AssistantMessage: FC = () => {
  const isInline = useIsInlineTool();
  return (
  <>
    <CompactionDividerAbove />
    <MessagePrimitive.Root className="mb-4 flex w-full flex-col text-base leading-relaxed text-fg">
    <MessagePrimitive.GroupedParts
      groupBy={(part) => {
        if (part.type === "reasoning") return ["group-chainOfThought", "group-reasoning"];
        if (part.type === "tool-call" && !isInline(part.toolName)) {
          return ["group-chainOfThought", "group-tool"];
        }
        return null;
      }}
    >
      {({ part, children }) => {
        switch (part.type) {
          case "group-chainOfThought":
            return <div className="my-2">{children}</div>;
          case "group-reasoning":
            // The bid agent strips its thinking server-side; nothing to show.
            return null;
          case "group-tool":
            return (
              <>
                <ThreadPlan indices={part.indices} />
                <ToolActivityList indices={part.indices} />
                {children}
              </>
            );
          case "text":
            return <MarkdownText />;
          case "tool-call":
            return isInline(part.toolName) &&
              !part.isError &&
              part.status.type !== "incomplete" ? (
              <div className="mb-3">{part.toolUI}</div>
            ) : null;
          default:
            return null;
        }
      }}
    </MessagePrimitive.GroupedParts>
    <AssistantActionBar />
    <MessageError />
    <StalledRunNotice />
    </MessagePrimitive.Root>
  </>
  );
};

/**
 * Divider above the first message the agent still sees in full, once it has
 * compacted the conversation before it (PE-626). Rendered by both message
 * components, since either role can be the anchor.
 */
const CompactionDividerAbove: FC = () => {
  const messageId = useAuiState((state) => state.message.id);
  const { contextCompaction } = useAgentsChat();
  if (!contextCompaction || contextCompaction.anchorMessageId !== messageId) return null;
  return <ContextCompactedMarker />;
};

/**
 * The compaction divider itself. The summary is written for the agent, not
 * the user, so the row says only that compaction happened.
 */
const ContextCompactedMarker: FC = () => (
  <div
    role="separator"
    aria-label="Earlier conversation compacted"
    className="mb-3 flex items-center gap-3 py-1 text-xs text-muted-foreground"
  >
    <span aria-hidden className="h-px flex-1 bg-border" />
    <span className="whitespace-nowrap rounded-full border border-border bg-muted px-3 py-1 font-medium text-foreground/80">
      Earlier conversation compacted
    </span>
    <span aria-hidden className="h-px flex-1 bg-border" />
  </div>
);

/** Shown while the backend reports it is compacting the thread (PE-626). */
const ContextCompactionActivity: FC = () => {
  const { isContextCompacting } = useAgentsChat();
  if (!isContextCompacting) return null;
  return (
    <div
      role="status"
      aria-label="Automatically compacting context"
      className="mb-4 flex items-center gap-1 px-1"
    >
      <span className="size-2 animate-pulse rounded-full bg-orange" />
      <span className="text-xs text-muted-foreground">Compacting context…</span>
    </div>
  );
};

/** A cursor between the user's message and the first streamed token. */
const PendingAssistantMessage: FC = () => {
  const { isContextCompacting } = useAgentsChat();
  const showPending = useAuiState((state) => {
    if (!state.thread.isRunning) return false;
    return state.thread.messages.at(-1)?.role === "user";
  });
  if (!showPending || isContextCompacting) return null;
  return (
    <div role="status" aria-label="Working" className="mb-4 flex items-center gap-1 px-1">
      <span className="size-2 animate-pulse rounded-full bg-accent" />
      <span className="text-xs text-muted-foreground">Working…</span>
    </div>
  );
};

const AssistantActionBar: FC = () => (
  <ActionBarPrimitive.Root
    hideWhenRunning
    autohide="not-last"
    className="mt-1 flex items-center gap-1 text-muted-foreground"
  >
    <ActionBarPrimitive.Copy asChild>
      <TooltipIconButton tooltip="Copy" className="size-7">
        <MessagePrimitive.If copied>
          <CheckIcon className="size-3.5" />
        </MessagePrimitive.If>
        <MessagePrimitive.If copied={false}>
          <CopyIcon className="size-3.5" />
        </MessagePrimitive.If>
      </TooltipIconButton>
    </ActionBarPrimitive.Copy>
  </ActionBarPrimitive.Root>
);

/**
 * The run ended while a tool call was still open and no error was reported:
 * say so, and offer to ask again. (A reported error shows in MessageError.)
 */
const StalledRunNotice: FC = () => {
  const stalled = useThreadStalled();
  const hanging = useAuiState((state) => {
    if (!state.message.isLast || state.message.role !== "assistant") return false;
    if (state.message.status?.type === "incomplete") return false;
    // The state's part type doesn't declare status; the runtime sets it.
    return state.message.content.some((part) => {
      const status = (part as { status?: { type?: string } }).status?.type;
      return part.type === "tool-call" && (status === "running" || status === "requires-action");
    });
  });
  const lastPrompt = useAuiState((state) => {
    const user = [...state.thread.messages].reverse().find((m) => m.role === "user");
    return (
      user?.content
        .filter((part): part is { type: "text"; text: string } => part.type === "text")
        .map((part) => part.text)
        .join("\n")
        .trim() ?? ""
    );
  });
  const runtime = useThreadRuntime();
  if (!stalled || !hanging) return null;
  return (
    <ThreadBanner tone="error">
      io.ai stopped before it finished.
      {lastPrompt && (
        <>
          {" "}
          <button
            type="button"
            className="font-semibold underline"
            onClick={() => {
              trackMessageSent("retry");
              runtime.append(lastPrompt);
            }}
          >
            Ask again
          </button>
        </>
      )}
    </ThreadBanner>
  );
};

const MessageError: FC = () => (
  <MessagePrimitive.Error>
    <ErrorPrimitive.Root className="mt-2 rounded-md border border-destructive bg-destructive/10 p-2 text-xs text-destructive">
      <ErrorPrimitive.Message />
    </ErrorPrimitive.Root>
  </MessagePrimitive.Error>
);

export const ThreadBanner: FC<{ tone?: "error" | "info"; children: ReactNode }> = ({
  tone = "info",
  children
}) => (
  <div
    className={cn(
      "mt-2 w-full rounded-sm border px-2.5 py-2 text-xs",
      tone === "error" ? "border-danger/30 bg-danger-soft text-danger" : "border-line bg-subtle text-fg-2"
    )}
  >
    {children}
  </div>
);
